"use client";

import { Compass, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function VisitTheStudio() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const tickerItems = Array.from({
    length: 12
  }, (_, i) => i);
  return <section data-section-id="4901" id="visit-the-studio" className="dark bg-background text-foreground relative overflow-hidden pt-24 md:pt-32 lg:pt-40 pb-20 md:pb-28 lg:pb-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        {}
        <div ref={leftRef} className={`mb-12 md:mb-16 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-primary">
            — Fig. IV · The Studio
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className={`lg:col-span-5 transition-all duration-1000 ease-out delay-100 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <h2 className="font-default text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight mb-10 md:mb-12" style={{
            fontFamily: "var(--font-serif)"
          }}>
              Visit us at{" "}
              <em className="italic font-light text-primary" style={{
              fontFamily: "var(--font-serif)"
            }}>
                the salon
              </em>
            </h2>

            {}
            <address className="not-italic font-default text-2xl md:text-[1.75rem] leading-[1.4] font-light text-foreground/90 mb-10" style={{
            fontFamily: "var(--font-serif)",
            fontStyle: "italic"
          }}>
              <div>128 Velvet Avenue</div>
              <div>New York, New York 10012</div>
              <div>SoHo District</div>
            </address>

            <div className="h-px w-full bg-border mb-8" />

            {}
            <div className="mb-10">
              <div className="font-mono text-xs tracking-[0.25em] uppercase text-primary mb-4">
                Studio Hours
              </div>
              <div className="space-y-2">
                <div className="font-mono text-xs md:text-sm tracking-[0.15em] uppercase text-foreground/80">
                  Tuesday — Saturday · 10AM — 7PM
                </div>
                <div className="font-mono text-xs md:text-sm tracking-[0.15em] uppercase text-foreground/80">
                  Sunday & Monday · Closed
                </div>
              </div>
            </div>

            <div className="h-px w-full bg-border mb-8" />

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-12">
              <div>
                <div className="font-mono text-xs tracking-[0.25em] uppercase text-primary mb-3">
                  Getting Here
                </div>
                <ul className="space-y-1.5 font-mono text-[11px] md:text-xs tracking-[0.1em] uppercase text-foreground/70 leading-relaxed">
                  <li>Subway: N, R, W to Prince St</li>
                  <li>Valet Parking Available</li>
                  <li>Entrance on Mercer St</li>
                </ul>
              </div>
              <div>
                <div className="font-mono text-xs tracking-[0.25em] uppercase text-primary mb-3">
                  Booking Info
                </div>
                <ul className="space-y-1.5 font-mono text-[11px] md:text-xs tracking-[0.1em] uppercase text-foreground/70 leading-relaxed">
                  <li>Consultations Required</li>
                  <li>Pricing Guide Available</li>
                </ul>
              </div>
            </div>

            {}
            <Button asChild variant="outline" size="lg" className="group bg-transparent border border-foreground text-foreground hover:bg-foreground hover:text-background transition-all duration-500 px-8 py-6 h-auto font-mono text-xs tracking-[0.25em] uppercase">
              <Link to="/BookPage">Download Pricing Guide</Link>
            </Button>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative border border-border overflow-hidden h-[420px] md:h-[480px] lg:h-[520px] bg-card">
              {}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="orchardGrid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <circle cx="20" cy="20" r="1" fill="var(--foreground)" opacity="0.08" />
                  </pattern>
                  <pattern id="paperGrain" width="200" height="200" patternUnits="userSpaceOnUse">
                    <rect width="200" height="200" fill="transparent" />
                    <circle cx="30" cy="50" r="0.5" fill="var(--foreground)" opacity="0.05" />
                    <circle cx="120" cy="80" r="0.5" fill="var(--foreground)" opacity="0.05" />
                    <circle cx="170" cy="160" r="0.5" fill="var(--foreground)" opacity="0.05" />
                    <circle cx="60" cy="180" r="0.5" fill="var(--foreground)" opacity="0.05" />
                  </pattern>
                </defs>

                {}
                <rect width="800" height="600" fill="var(--card)" />
                <rect width="800" height="600" fill="url(#orchardGrid)" />

                {}
                <path d="M 380 0 Q 360 100 400 180 Q 440 260 410 340 Q 380 420 420 500 Q 450 560 430 600" stroke="var(--primary)" strokeWidth="3" fill="none" opacity="0.4" />
                <path d="M 380 0 Q 360 100 400 180 Q 440 260 410 340 Q 380 420 420 500 Q 450 560 430 600" stroke="var(--primary)" strokeWidth="14" fill="none" opacity="0.08" />

                {}
                <path d="M 100 200 Q 250 180 380 200" stroke="var(--foreground)" strokeWidth="0.5" fill="none" opacity="0.2" />
                <path d="M 80 240 Q 240 220 380 240" stroke="var(--foreground)" strokeWidth="0.5" fill="none" opacity="0.2" />
                <path d="M 60 280 Q 230 260 380 280" stroke="var(--foreground)" strokeWidth="0.5" fill="none" opacity="0.2" />
                <path d="M 440 220 Q 580 200 720 220" stroke="var(--foreground)" strokeWidth="0.5" fill="none" opacity="0.2" />
                <path d="M 450 260 Q 590 240 730 260" stroke="var(--foreground)" strokeWidth="0.5" fill="none" opacity="0.2" />
                <path d="M 460 300 Q 600 280 740 300" stroke="var(--foreground)" strokeWidth="0.5" fill="none" opacity="0.2" />

                {}
                <path d="M 0 380 L 800 360" stroke="var(--foreground)" strokeWidth="1" fill="none" opacity="0.25" strokeDasharray="2 4" />
                <path d="M 200 0 L 220 600" stroke="var(--foreground)" strokeWidth="1" fill="none" opacity="0.25" strokeDasharray="2 4" />
                <path d="M 600 0 L 580 600" stroke="var(--foreground)" strokeWidth="1" fill="none" opacity="0.25" strokeDasharray="2 4" />

                {}
                <text x="180" y="180" fontFamily="var(--font-mono)" fontSize="9" fill="var(--foreground)" opacity="0.4" letterSpacing="2">
                  CHELSEA
                </text>
                <text x="540" y="180" fontFamily="var(--font-mono)" fontSize="9" fill="var(--foreground)" opacity="0.4" letterSpacing="2">
                  TRIBECA
                </text>
                <text x="180" y="480" fontFamily="var(--font-mono)" fontSize="9" fill="var(--foreground)" opacity="0.4" letterSpacing="2">
                  CHINATOWN
                </text>
                <text x="540" y="480" fontFamily="var(--font-mono)" fontSize="9" fill="var(--foreground)" opacity="0.4" letterSpacing="2">
                  BROOKLYN
                </text>

                {}
                <text x="440" y="290" fontFamily="var(--font-mono)" fontSize="10" fill="var(--foreground)" opacity="0.7" letterSpacing="2">
                  SOHO
                </text>

                {}
                <g opacity="0.25">
                  <circle cx="470" cy="340" r="2" fill="var(--primary)" />
                  <circle cx="485" cy="345" r="2" fill="var(--primary)" />
                  <circle cx="475" cy="355" r="2" fill="var(--primary)" />
                  <circle cx="495" cy="335" r="2" fill="var(--primary)" />
                  <circle cx="490" cy="360" r="2" fill="var(--primary)" />
                </g>

                {}
                <rect width="800" height="600" fill="url(#paperGrain)" />
              </svg>

              {}
              <div className="absolute top-6 right-6 w-16 h-16 flex items-center justify-center">
                <svg viewBox="0 0 64 64" className="w-full h-full opacity-50">
                  <circle cx="32" cy="32" r="28" stroke="var(--foreground)" strokeWidth="0.5" fill="none" />
                  <circle cx="32" cy="32" r="20" stroke="var(--foreground)" strokeWidth="0.5" fill="none" />
                  <path d="M 32 6 L 35 32 L 32 58 L 29 32 Z" fill="var(--foreground)" opacity="0.6" />
                  <path d="M 6 32 L 32 29 L 58 32 L 32 35 Z" fill="var(--foreground)" opacity="0.3" />
                  <text x="32" y="14" textAnchor="middle" fontFamily="var(--font-mono)" fontSize="6" fill="var(--foreground)" opacity="0.8">N</text>
                </svg>
              </div>

              {}
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="absolute group p-0 h-auto w-auto bg-transparent hover:bg-transparent" style={{
                    left: "56%",
                    top: "52%",
                    transform: "translate(-50%, -100%)"
                  }} aria-label="Salon location">
                      <div className="relative flex flex-col items-center">
                        <div className="absolute -inset-4 bg-primary opacity-20 animate-ping" />
                        <MapPin className="w-8 h-8 text-primary drop-shadow-md transition-transform duration-300 group-hover:scale-110" fill="var(--primary)" strokeWidth={1.5} />
                      </div>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-card text-card-foreground border border-border px-4 py-3 font-mono text-[10px] tracking-[0.15em] uppercase max-w-[220px]">
                    <div className="space-y-0.5">
                      <div className="text-primary">Velvet Shears</div>
                      <div>128 Velvet Avenue</div>
                      <div>New York, NY 10012</div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {}
            <div className="mt-4 flex items-center gap-3">
              <Compass className="w-3 h-3 text-primary" strokeWidth={1.5} />
              <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-foreground/60">
                N° 40.7245° N · 73.9980° W
              </span>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="mt-20 md:mt-28 border-y border-border py-6 overflow-hidden">
        <div className="flex whitespace-nowrap animate-[ticker_40s_linear_infinite]">
          {tickerItems.map((item, i) => <div key={i} data-index={i} className="flex items-center font-mono text-xs md:text-sm tracking-[0.35em] uppercase text-foreground/40 px-8">
              <span>Precision Cuts</span>
              <span className="mx-8 text-primary">·</span>
            </div>)}
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>;
}